import { differenceInCalendarDays, isValid, parseISO } from 'date-fns';
import { Injectable } from '@angular/core';
import { BookingChain, BookingChainLink, BookingChainMarks, BookingChainTruncation } from './booking-chain.model';
import { IBookingModel } from './booking-model.interface';

/** Felső korlát a láncbejárásra; védelem hibás adat ellen, nem üzleti szabály. */
const MAX_CHAIN_LENGTH = 20;
const IRI_ID_PATTERN = /\/(\d+)$/;

type ChainDirection = 'previousBooking' | 'nextBooking';

interface ChainWalk {
  readonly nodes: IBookingModel[];
  readonly truncation?: BookingChainTruncation;
}

/**
 * A foglalás-láncot a backend által beágyazott gráfból építi fel — HTTP nélkül.
 * A backend elfogadja az önmagára mutató és körkörös hivatkozást, ezért a bejárás ciklus-biztos.
 */
@Injectable({
  providedIn: 'root'
})
export class BookingChainService {
  buildChain(booking: IBookingModel, currentId: number = booking.id): BookingChain {
    const seen = new Set<number>([booking.id]);
    const backward = this.walk(booking, 'previousBooking', seen);
    const forward = this.walk(booking, 'nextBooking', seen);
    const ordered = [...backward.nodes.slice().reverse(), booking, ...forward.nodes];
    const links = ordered.map((node) => this.toLink(node, node.id === currentId));
    return {
      links,
      currentIndex: links.findIndex((link) => link.isCurrent),
      totalNights: links.reduce((sum, link) => sum + link.nights, 0),
      truncatedAt: backward.truncation ?? forward.truncation
    };
  }

  marksFor(booking: IBookingModel): BookingChainMarks {
    const isExtension = !!booking.previousBooking;
    const isExtended = !!booking.nextBooking;
    if (!isExtension && !isExtended) {
      return { isExtension: false, isExtended: false };
    }
    const chain = this.buildChain(booking);
    return { isExtension, isExtended, position: { index: chain.currentIndex + 1, total: chain.links.length } };
  }

  tailOf(chain: BookingChain): BookingChainLink {
    return chain.links[chain.links.length - 1];
  }

  idFromIri(iri: string): number | null {
    const match = IRI_ID_PATTERN.exec(iri);
    return match ? Number(match[1]) : null;
  }

  private walk(from: IBookingModel, direction: ChainDirection, seen: Set<number>): ChainWalk {
    const nodes: IBookingModel[] = [];
    let cursor = from[direction];
    while (cursor && nodes.length < MAX_CHAIN_LENGTH) {
      if (typeof cursor === 'string') {
        return { nodes, truncation: this.truncationFor(direction, cursor, seen) };
      }
      if (seen.has(cursor.id)) {
        return { nodes };
      }
      seen.add(cursor.id);
      nodes.push(cursor);
      cursor = cursor[direction];
    }
    return { nodes };
  }

  /** IRI a lánc végén: ha már látott szemre mutat, csak körkörösség-törés — nem hiányzik semmi. */
  private truncationFor(direction: ChainDirection, iri: string, seen: Set<number>): BookingChainTruncation | undefined {
    const id = this.idFromIri(iri);
    if (id === null || seen.has(id)) {
      return undefined;
    }
    return { side: direction === 'previousBooking' ? 'head' : 'tail', iri };
  }

  private toLink(booking: IBookingModel, isCurrent: boolean): BookingChainLink {
    const startDate = parseISO(booking.start_date ?? '');
    const endDate = parseISO(booking.end_date ?? '');
    return {
      id: booking.id,
      iri: booking['@id'] ?? '',
      startDate,
      endDate,
      nights: this.nightsBetween(startDate, endDate),
      guestName: booking.name ?? '',
      flatLabel: this.flatLabelOf(booking),
      flats: booking.flats ?? [],
      statusName: booking.bookingStatus?.name ?? '',
      isCurrent
    };
  }

  private nightsBetween(startDate: Date, endDate: Date): number {
    if (!isValid(startDate) || !isValid(endDate)) {
      return 0;
    }
    return Math.max(0, differenceInCalendarDays(endDate, startDate));
  }

  private flatLabelOf(booking: IBookingModel): string | null {
    const flat = booking.flats?.[0];
    if (!flat) {
      return null;
    }
    return `${flat.name ?? ''} ${flat.flat_number ?? ''}`.trim() || null;
  }
}
